import { Group, GroupProps } from '@mantine/core';
import { Attributes } from '../molecues/Attributes';
import { Workflow, WorkflowConfig } from '@/app/workflows/actions/interfaces';
import { getSimplificationMethodLabel } from './utils/simplification';
import { getMeshCompressionLabel } from './utils/meshCompression';
import { DescriptionEditable } from '../molecues/DescriptionEditable';
import { getMeshResolution } from './utils/getMeshResolution';
import { getTextureResolution } from './utils/getTextureResolution';
import { useMediaQuery } from '@mantine/hooks';
import { useWorkflow } from '../../../contexts/getWorkflow';

enum Labels {
  MeshResolution = 'Mesh resolution:',
  MeshCompression = 'Mesh compression:',
  TextureResolution = 'Texture resolution:',
  TextureFormat = 'Texture format:',
  SimplificationMethod = 'Simplification Method:',
  BakeAOMap = 'Bake AO Map:',
  BakeNormalMap = 'Bake Normal Map:',
  FileFormats = '3D File Formats:',
}

const getWorkflowDetails = (workflow: Workflow) => {
  const config = workflow.config as WorkflowConfig;
  const { compressionAndExport, assetSimplification } = config;
  const { fileExports } = compressionAndExport;
  const { meshCompressionMethod, textureFormat } = fileExports[0];
  const method = assetSimplification?.meshDecimation.method;
  const bakeAOMap = assetSimplification?.materialAndTextureBaking?.bakeAOMap;
  const bakeNormalMap = assetSimplification?.materialAndTextureBaking?.bakeNormalMap;

  const MeshResolution = getMeshResolution(config);
  const MeshCompression = getMeshCompressionLabel(meshCompressionMethod);
  const TextureResolution = getTextureResolution(config);
  const TextureFormat = textureFormat?.baseColor ?? '';
  const SimplificationMethod = getSimplificationMethodLabel(method);
  const BakeAOMap = bakeAOMap ? 'On' : 'Off';
  const BakeNormalMap = bakeNormalMap ? 'On' : 'Off';
  const FileFormats = config.compressionAndExport.fileExports
    .map((file) => file.fileType.toLocaleUpperCase())
    .join('+');

  return [
    {
      label: Labels.MeshResolution,
      value: MeshResolution,
    },
    {
      label: Labels.MeshCompression,
      value: MeshCompression,
    },
    {
      label: Labels.TextureResolution,
      value: TextureResolution,
    },
    {
      label: Labels.TextureFormat,
      value: TextureFormat,
    },
    {
      label: Labels.SimplificationMethod,
      value: SimplificationMethod,
    },
    {
      label: Labels.BakeAOMap,
      value: BakeAOMap,
    },
    {
      label: Labels.BakeNormalMap,
      value: BakeNormalMap,
    },
    {
      label: Labels.FileFormats,
      value: FileFormats,
    },
  ];
};

export const Details = ({
  workflow,
  wrappadable = false,
  ...props
}: { workflow: Workflow; wrappadable?: boolean } & GroupProps) => {
  const { getWorkflow } = useWorkflow();
  const details = getWorkflowDetails(getWorkflow() ?? workflow);
  const matches = useMediaQuery('(min-width: 1100px)');
  const isWrappable = !matches && wrappadable;

  return (
    <Group justify='space-between' grow={!isWrappable} gap='xs' {...props} wrap='wrap'>
      <DescriptionEditable workflow={workflow} />
      <Attributes rows={details} />
    </Group>
  );
};
